// -----------------------------------------------------------------------------
// Per-template typography defaults (iter 44 — 8-pt Universal + Date/Location)
// -----------------------------------------------------------------------------
// Universal spec:
//   • Font family: Inter across every category.
//   • Font size: 8 pt for every category — EXCEPT Resume Name (each template
//     picks a display size 16-28pt so the name still pops).
//   • Font weight: Regular (400) — Section Headers, Job Title, and Resume
//     Name step UP per spec (Section Headers = 600, Job Title = 600, Resume
//     Name = 700+).
//   • Line spacing:
//       – 1.3 for body descriptions (workDesc, description, skills, projects,
//         education, certifications, additional, contactInfo, company, date,
//         location).
//       – 1.8 for Section Headers + Job Titles.
//     Line-height is baked into the per-category CSS emission in
//     ResumePreview.js — this file just carries the per-category size /
//     weight / colour defaults.
//   • Font colour: Black (#0a0a0a) — EXCEPT the two dark-sidebar templates:
//       – Two-Column sidebar (Contact, Skills, Education, Certifications,
//         Additional) AND Resume Name (which renders in the sidebar too) all
//         default to WHITE.
//       – Creative Skills chips default to WHITE (the chips themselves have
//         a dark background); everything else on Creative defaults to
//         BLACK, including Contact and Education which render on the light
//         side of Creative's layout.
// -----------------------------------------------------------------------------

const BLACK = "#0a0a0a";
const WHITE = "#ffffff";

const bodyCat = (color = BLACK) => ({
  family: "inherit",
  size: 8,
  weight: 400,
  color,
});

const UNIVERSAL_BASE = {
  fontFamily: "Inter",
  descriptionFontFamily: "Inter",
  fontScale: 1,
  // Global lineSpacing acts on non-tagged text only; per-category line-
  // heights are emitted via the injected CSS. 1.3 matches the descriptions
  // default so untagged text sits at the same rhythm.
  lineSpacing: 1.3,
  h1FontWeight: 700,
  h2FontWeight: 600,
  bodyFontWeight: 400,
  h1Color: BLACK,
  h2Color: BLACK,
  resumeNameFontSize: 22,
  resumeNameFontFamily: "inherit",
  sectionHeaderFontSize: 8,
  sectionHeaderFontFamily: "inherit",
  descriptionFontSize: 8,
  descriptionFontWeight: 400,
  descriptionColor: BLACK,
  contactInfoFontSize: 8,
  contactInfoFontWeight: 400,
  contactInfoColor: BLACK,
  contactInfoFontFamily: "inherit",
  jobTitleFontSize: 8,
  jobTitleFontWeight: 600,
  jobTitleColor: BLACK,
  jobTitleFontFamily: "inherit",
  companyFontSize: 8,
  companyFontWeight: 400,
  companyColor: BLACK,
  companyFontFamily: "inherit",
  workDescriptionFontSize: 8,
  workDescriptionFontWeight: 400,
  workDescriptionColor: BLACK,
  workDescriptionFontFamily: "inherit",
  // NEW iter 44 — Date + Location categories (both live inside Work Exp).
  dateFontSize: 8,
  dateFontWeight: 400,
  dateColor: BLACK,
  dateFontFamily: "inherit",
  locationFontSize: 8,
  locationFontWeight: 400,
  locationColor: BLACK,
  locationFontFamily: "inherit",
  bodyTypography: {
    skills:         bodyCat(),
    education:      bodyCat(),
    projects:       bodyCat(),
    certifications: bodyCat(),
    additional:     bodyCat(),
  },
};

const MODERN       = { ...UNIVERSAL_BASE, resumeNameFontSize: 24 };
const CLASSIC      = { ...UNIVERSAL_BASE, resumeNameFontSize: 26 };
const MINIMAL      = { ...UNIVERSAL_BASE, resumeNameFontSize: 20 };
const EXECUTIVE    = { ...UNIVERSAL_BASE, resumeNameFontSize: 22, h1FontWeight: 800 };
const ATS_PLAIN    = { ...UNIVERSAL_BASE, resumeNameFontSize: 18 };
const ATS_COMPACT  = { ...UNIVERSAL_BASE, resumeNameFontSize: 16 };
const PHOTO_HEADER = { ...UNIVERSAL_BASE, resumeNameFontSize: 22 };

// Creative — has a LIGHT sidebar with a Statement-panel layout. The only
// element that renders on dark is each individual skill chip which paints
// its own background. So Contact + Education + everything else default to
// BLACK; Skills defaults to WHITE (chip text sits on the dark chip fill).
const CREATIVE = {
  ...UNIVERSAL_BASE,
  resumeNameFontSize: 28,
  h1FontWeight: 800,
  contactInfoColor: BLACK,           // ← fix per user feedback iter 44
  bodyTypography: {
    skills:         bodyCat(WHITE),  // chips have dark backgrounds
    education:      bodyCat(BLACK),  // renders on light sidebar
    projects:       bodyCat(BLACK),
    certifications: bodyCat(BLACK),
    additional:     bodyCat(BLACK),
  },
};

// Two-Column — full dark sidebar. Resume Name, Contact, Skills, Education,
// Certifications, Additional (Languages) ALL live in that sidebar → WHITE
// defaults. Main-column categories (Job Title, Company, Date, Location,
// Work Description, Section Headers, Description, Projects) stay BLACK.
const TWO_COLUMN = {
  ...UNIVERSAL_BASE,
  resumeNameFontSize: 24,
  h1Color: WHITE,                    // ← fix per user feedback iter 44
  contactInfoColor: WHITE,
  bodyTypography: {
    skills:         bodyCat(WHITE),  // sidebar
    education:      bodyCat(WHITE),  // sidebar
    projects:       bodyCat(BLACK),  // main column
    certifications: bodyCat(WHITE),  // sidebar
    additional:     bodyCat(WHITE),  // sidebar (Languages)
  },
};

export const TEMPLATE_TYPOGRAPHY_DEFAULTS = {
  modern:        MODERN,
  classic:       CLASSIC,
  minimal:       MINIMAL,
  creative:      CREATIVE,
  executive:     EXECUTIVE,
  "ats-plain":   ATS_PLAIN,
  "ats-compact": ATS_COMPACT,
  "two-column":  TWO_COLUMN,
  "photo-header": PHOTO_HEADER,
};

/** Returns a full defaults object for the given template id (falls back to Modern). */
export function getTemplateTypographyDefaults(templateId) {
  return TEMPLATE_TYPOGRAPHY_DEFAULTS[templateId] || MODERN;
}
